import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Checkbox,
  Form,
  Input,
  Space,
  Typography,
  message,
  Spin,
  Alert,
  Divider,
  Progress,
  Tag,
  Timeline,
  Collapse,
  InputNumber,
  Switch,
  Row,
  Col,
  Statistic,
  Modal
} from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  LinkOutlined,
  FileImageOutlined,
  CodeOutlined,
  RocketOutlined,
  GoogleOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useTenantStore } from '../stores/useTenantStore';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

interface AuditTask {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: 'data' | 'onpage' | 'technical' | 'performance' | 'indexing';
}

interface AuditConfig {
  enabled_tasks: string[];
  auto_apply_fixes: boolean;
  ahrefs_project_id: string;
  redirect_settings: any;
  meta_settings: any;
  title_settings: any;
  h1_settings: any;
  image_settings: any;
  broken_link_settings: any;
  performance_settings: any;
  reindex_settings: any;
}

interface AuditRun {
  run_id: string;
  status: string;
  tenant_id: string;
  website: string;
  total_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
  started_at: string;
  completed_at?: string;
  task_results: TaskResult[];
}

interface TaskResult {
  task_type: string;
  status: string;
  items_processed: number;
  items_fixed: number;
  items_failed: number;
  details?: any;
  error?: string;
  started_at?: string;
  completed_at?: string;
}

const AUDIT_TASKS: AuditTask[] = [
  {
    id: 'fetch_ahrefs_data',
    name: 'Fetch Ahrefs Site Audit Data',
    description: 'Retrieve latest audit data from Ahrefs Site Audit',
    icon: <GlobalOutlined />,
    category: 'data'
  },
  {
    id: 'fix_redirects',
    name: 'Fix Redirect Chains',
    description: 'Update redirects to point directly to final destinations',
    icon: <LinkOutlined />,
    category: 'technical'
  },
  {
    id: 'fix_meta_descriptions',
    name: 'Fix Meta Descriptions',
    description: 'Generate and apply optimized meta descriptions (120-160 chars)',
    icon: <CodeOutlined />,
    category: 'onpage'
  },
  {
    id: 'fix_titles',
    name: 'Fix Title Tags',
    description: 'Generate and apply optimized title tags (30-60 chars)',
    icon: <CodeOutlined />,
    category: 'onpage'
  },
  {
    id: 'fix_h1s',
    name: 'Fix H1 Tags',
    description: 'Ensure single H1 tag per page with keyword inclusion',
    icon: <CodeOutlined />,
    category: 'onpage'
  },
  {
    id: 'fix_image_alt_text',
    name: 'Fix Image Alt Text',
    description: 'Generate descriptive alt text for images (<125 chars)',
    icon: <FileImageOutlined />,
    category: 'onpage'
  },
  {
    id: 'optimize_images',
    name: 'Optimize Images',
    description: 'Compress images and convert to WebP format',
    icon: <FileImageOutlined />,
    category: 'performance'
  },
  {
    id: 'fix_schema',
    name: 'Fix Schema Markup',
    description: 'Validate and fix schema.org markup errors',
    icon: <CodeOutlined />,
    category: 'technical'
  },
  {
    id: 'replace_broken_links',
    name: 'Replace Broken Links',
    description: 'Find and suggest replacements for broken links via web search',
    icon: <LinkOutlined />,
    category: 'technical'
  },
  {
    id: 'detect_slow_pages',
    name: 'Detect Slow Pages',
    description: 'Identify pages with load times > 3 seconds',
    icon: <ThunderboltOutlined />,
    category: 'performance'
  },
  {
    id: 'reindex_google',
    name: 'Request Google Reindexing',
    description: 'Submit URLs to Google Search Console for reindexing',
    icon: <GoogleOutlined />,
    category: 'indexing'
  },
  {
    id: 'reindex_bing',
    name: 'Request Bing Reindexing',
    description: 'Submit URLs to Bing Webmaster Tools for reindexing',
    icon: <GlobalOutlined />,
    category: 'indexing'
  }
];

const ComprehensiveSiteAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentTenant, tenants } = useTenantStore();
  const tenantObject = tenants.find(t => t.website === currentTenant);
  const [form] = Form.useForm();
  const [running, setRunning] = useState(false);
  const [currentRun, setCurrentRun] = useState<AuditRun | null>(null);
  const [enabledTasks, setEnabledTasks] = useState<string[]>(AUDIT_TASKS.map(t => t.id));
  const [autoApply, setAutoApply] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!currentRun || currentRun.status === 'completed' || currentRun.status === 'failed') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${apiBaseUrl}/comprehensive-audit/runs/${currentRun.run_id}`);
        const updatedRun = await response.json();
        setCurrentRun(updatedRun);

        if (updatedRun.status === 'completed' || updatedRun.status === 'failed') {
          setRunning(false);
          if (updatedRun.status === 'completed') {
            message.success('Audit completed successfully!');
          } else {
            message.error('Audit failed. Check the results for details.');
          }
        }
      } catch (error) {
        console.error('Failed to poll audit status', error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [currentRun]);

  const handleRunAudit = async () => {
    try {
      const values = await form.validateFields();

      if (enabledTasks.length === 0) {
        message.error('Please select at least one task to run');
        return;
      }

      setRunning(true);

      const config: AuditConfig = {
        enabled_tasks: enabledTasks,
        auto_apply_fixes: autoApply,
        ahrefs_project_id: values.ahrefs_project_id,
        redirect_settings: {
          max_chain_length: values.max_redirect_chain || 3,
          fix_temporary_redirects: true
        },
        meta_settings: {
          min_length: values.meta_min_length || 120,
          max_length: values.meta_max_length || 160,
          include_keywords: true
        },
        title_settings: {
          min_length: values.title_min_length || 30,
          max_length: values.title_max_length || 60,
          include_keywords: true
        },
        h1_settings: {
          ensure_single_h1: true,
          include_keyword: true
        },
        image_settings: {
          max_file_size_kb: values.max_image_size || 200,
          preferred_format: 'webp',
          compression_quality: values.image_quality || 85,
          generate_alt_text: true
        },
        broken_link_settings: {
          web_search_enabled: true,
          avoid_competitors: true,
          max_replacements: 50
        },
        performance_settings: {
          max_load_time_seconds: values.max_load_time || 3.0,
          report_to_developer: true,
          enroll_in_slow_page_agent: false
        },
        reindex_settings: {
          reindex_after_fixes: true,
          batch_size: 100
        }
      };

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/comprehensive-audit/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantObject?.website,
          website: values.website,
          config
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start audit');
      }

      const data = await response.json();
      setCurrentRun(data);
      message.success('Audit started successfully!');
    } catch (error) {
      console.error('Failed to start audit', error);
      message.error('Failed to start audit');
      setRunning(false);
    }
  };

  const handleToggleTask = (taskId: string) => {
    setEnabledTasks(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    setEnabledTasks(AUDIT_TASKS.map(t => t.id));
  };

  const handleSelectNone = () => {
    setEnabledTasks([]);
  };

  const groupedTasks = AUDIT_TASKS.reduce((acc, task) => {
    if (!acc[task.category]) {
      acc[task.category] = [];
    }
    acc[task.category].push(task);
    return acc;
  }, {} as Record<string, AuditTask[]>);

  const categoryNames = {
    data: 'Data Fetching',
    onpage: 'On-Page SEO',
    technical: 'Technical SEO',
    performance: 'Performance',
    indexing: 'Search Engine Indexing'
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <RobotOutlined /> Comprehensive Site Audit
      </Title>
      <Paragraph>
        Run a complete technical SEO audit with granular control over which tasks to execute.
        Powered by Ahrefs Site Audit API and AI-driven fixes.
      </Paragraph>

      <Alert
        message="Feature On Hold"
        description="This Ahrefs-based audit system is currently on hold. We are consolidating to a single Screaming Frog-based audit system. Please use the Technical Audit feature instead."
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                website: tenantObject?.website || currentTenant || '',
                meta_min_length: 120,
                meta_max_length: 160,
                title_min_length: 30,
                title_max_length: 60,
                max_image_size: 200,
                image_quality: 85,
                max_load_time: 3.0,
                max_redirect_chain: 3
              }}
            >
              <Form.Item
                label="Website URL"
                name="website"
                rules={[{ required: true, message: 'Please enter website URL' }]}
              >
                <Input placeholder="https://example.com" prefix={<GlobalOutlined />} />
              </Form.Item>

              <Form.Item
                label="Ahrefs Project ID"
                name="ahrefs_project_id"
                tooltip="Find this in your Ahrefs Site Audit project settings"
              >
                <Input placeholder="Enter Ahrefs project ID" />
              </Form.Item>

              <Divider>Task Selection</Divider>

              <Space style={{ marginBottom: 16 }}>
                <Button size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleSelectNone}>
                  Select None
                </Button>
                <Text type="secondary">({enabledTasks.length} tasks selected)</Text>
              </Space>

              {Object.entries(groupedTasks).map(([category, tasks]) => (
                <div key={category} style={{ marginBottom: 24 }}>
                  <Title level={5}>{categoryNames[category as keyof typeof categoryNames]}</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {tasks.map(task => (
                      <Card
                        key={task.id}
                        size="small"
                        style={{
                          cursor: 'pointer',
                          border: enabledTasks.includes(task.id) ? '2px solid #1890ff' : '1px solid #d9d9d9'
                        }}
                        onClick={() => handleToggleTask(task.id)}
                      >
                        <Space>
                          <Checkbox checked={enabledTasks.includes(task.id)} onChange={() => handleToggleTask(task.id)} />
                          <span style={{ fontSize: 20 }}>{task.icon}</span>
                          <div>
                            <Text strong>{task.name}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {task.description}
                            </Text>
                          </div>
                        </Space>
                      </Card>
                    ))}
                  </Space>
                </div>
              ))}

              <Divider>Execution Settings</Divider>

              <Form.Item>
                <Space>
                  <Switch checked={autoApply} onChange={setAutoApply} />
                  <Text strong>Auto-Apply Fixes</Text>
                  <Text type="secondary">(Apply fixes automatically without manual review)</Text>
                </Space>
              </Form.Item>

              <Button
                type="link"
                icon={<SettingOutlined />}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
              </Button>

              {showAdvanced && (
                <Collapse ghost>
                  <Panel header="Meta Description Settings" key="meta">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Min Length" name="meta_min_length">
                          <InputNumber min={50} max={200} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Max Length" name="meta_max_length">
                          <InputNumber min={50} max={200} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>

                  <Panel header="Title Tag Settings" key="title">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Min Length" name="title_min_length">
                          <InputNumber min={20} max={70} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Max Length" name="title_max_length">
                          <InputNumber min={20} max={70} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>

                  <Panel header="Image Optimization Settings" key="image">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Max Size (KB)" name="max_image_size">
                          <InputNumber min={50} max={1000} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Compression Quality" name="image_quality">
                          <InputNumber min={50} max={100} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>

                  <Panel header="Performance Settings" key="performance">
                    <Form.Item label="Max Load Time (seconds)" name="max_load_time">
                      <InputNumber min={1} max={10} step={0.5} />
                    </Form.Item>
                  </Panel>

                  <Panel header="Redirect Settings" key="redirect">
                    <Form.Item label="Max Redirect Chain Length" name="max_redirect_chain">
                      <InputNumber min={1} max={10} />
                    </Form.Item>
                  </Panel>
                </Collapse>
              )}

              <Divider />

              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                loading={running}
                onClick={handleRunAudit}
                disabled={enabledTasks.length === 0}
                block
              >
                {running ? 'Running Audit...' : 'Run Comprehensive Audit'}
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {currentRun && (
            <Card title="Audit Progress">
              <Statistic
                title="Status"
                value={currentRun.status}
                prefix={
                  currentRun.status === 'completed' ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : currentRun.status === 'failed' ? (
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                  ) : (
                    <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  )
                }
              />

              <Divider />

              <Progress
                percent={Math.round((currentRun.completed_tasks / currentRun.total_tasks) * 100)}
                status={currentRun.status === 'failed' ? 'exception' : currentRun.status === 'completed' ? 'success' : 'active'}
              />

              <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                <Text>Tasks: {currentRun.completed_tasks} / {currentRun.total_tasks}</Text>
                <Text type="danger">Failed: {currentRun.failed_tasks}</Text>
              </Space>

              <Divider>Task Results</Divider>

              <Timeline mode="left">
                {currentRun.task_results?.map((result, index) => (
                  <Timeline.Item
                    key={index}
                    color={result.status === 'completed' ? 'green' : result.status === 'failed' ? 'red' : 'blue'}
                  >
                    <Text strong>{result.task_type.replace(/_/g, ' ').toUpperCase()}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {result.items_processed} processed, {result.items_fixed} fixed
                    </Text>
                    {result.error && (
                      <>
                        <br />
                        <Text type="danger" style={{ fontSize: 12 }}>
                          {result.error}
                        </Text>
                      </>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ComprehensiveSiteAuditPage;
